/**
 * guideStrategies (JSX)
 * ---------------------
 * Build arrow/guide points near the *last visible bar* using pixel offsets.
 * This avoids guides disappearing when priceToCoordinate() returns off-screen values.
 *
 * guideOnLastBar(opts)
 *  - overlay: {w,h}
 *  - logicalToX: (index) => px
 *  - priceToY: (price) => px
 *  - lastClose, lastHigh, lastLogical: numbers
 *  - xPx?: [p1,p2,p3]  -> pixels to the right of last bar
 *  - yUpPx?: [d1,d2,d3] -> negative values move upward from lastClose
 * 
* Functions that compute *guide geometry in overlay pixels*.
 * We keep existing exports you already use and add two more:
 *  - guideHorizontalAt({ priceToY, levelPrice, overlay })
 *  - guideTrendBetweenPivots({ logicalToX, priceToY, i1, i2, candles, overlay, yNudgePx })
 */

export function guideOnLastBar({
  overlay,
  logicalToX,
  priceToY,
  lastClose,
  lastHigh,
  lastLogical,
  xPx = [40, 120, 180],
  yUpPx = [0, -28, -44],
}) {
  const w = overlay?.w ?? 0;
  const h = overlay?.h ?? 0;

  // Base anchor at last bar (pixel space)
  const baseX =
    typeof logicalToX === "function" && typeof lastLogical === "number"
      ? logicalToX(lastLogical)
      : w * 0.78;
  const baseY =
    typeof priceToY === "function" && typeof lastClose === "number"
      ? priceToY(lastClose)
      : h * 0.62;

  // Fallbacks if projection failed
  const bx = typeof baseX === "number" ? baseX : w * 0.78;
  const by = typeof baseY === "number" ? baseY : h * 0.62;

  // Offsets in pixels (keep it deterministic across scales)
  let x1 = bx + (xPx[0] ?? 40);
  let x2 = bx + (xPx[1] ?? 120);
  let x3 = bx + (xPx[2] ?? 180);

  let y1 = by + (yUpPx[0] ?? 0);
  let y2 = by + (yUpPx[1] ?? -28);
  let y3 = by + (yUpPx[2] ?? -44);

  // Clamp to viewport so the arrow can’t escape
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const leftGuard = w * 0.60;           // keep it on the right half
  const rightGuard = w * 0.96;
  x1 = clamp(x1, leftGuard, rightGuard);
  x2 = clamp(x2, leftGuard, rightGuard);
  x3 = clamp(x3, leftGuard, rightGuard);

  const top = 8, bottom = h - 8;
  y1 = clamp(y1, top, bottom);
  y2 = clamp(y2, top, bottom);
  y3 = clamp(y3, top, bottom);

  return {
    start: { x: x1, y: y1 },
    mid:   { x: x2, y: y2 },
    end:   { x: x3, y: y3 },
  };
}

/**
 * guideUnderRunup (straight, low-to-last)
 * ---------------------------------------
 * Builds a straight guide/arrow that:
 *  - starts at the lowest low in the recent run-up
 *  - passes through the last bar’s low
 *  - extends to the right of the last bar
 *  - sits slightly *below* the candle tails via yOffsetPx
 *
 * Usage:
 *   const guide = guideUnderRunup({ overlay, logicalToX, priceToY, candles }, {
 *     lookback: 8,       // how many most-recent bars to search for the lowest low
 *     yOffsetPx: 4,      // push the guide down a few pixels under the tails
 *     rightPadPx: 120,   // how far to extend past the last bar
 *   });
 */
/**
 * guideStrategies.jsx (JS)
 * Guide builders for lessons. All functions return:
 *   { start:{x,y}, mid?:{x,y}, end:{x,y} }
 *
 * guideUnderRunup:
 * - Finds the lowest low in a recent window (lookback)
 * - Places a straight, rising baseline that runs under wick bottoms
 * - Extends past the last bar to the right (rightPadPx)
 */

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
function isNum(n){ return typeof n === "number" && Number.isFinite(n); }

export function guideUnderRunup(
  { overlay, logicalToX, priceToY, candles },
  opts = {}
) {
  const w = overlay?.w ?? 0;
  const h = overlay?.h ?? 0;
  const n = Array.isArray(candles) ? candles.length : 0;
  const lastIdx = Math.max(0, n - 1);

  const lookback  = isNum(opts.lookback)  ? opts.lookback  : 8;   // how far back to search
  const yOffsetPx = isNum(opts.yOffsetPx) ? opts.yOffsetPx : 4;   // sit a few px below wicks
  const rightPadPx= isNum(opts.rightPadPx)? opts.rightPadPx: 140; // extend to the right

  if (!n) {
    // Safe fallback diagonal inside the overlay
    return {
      start: { x: w * 0.15, y: h * 0.65 },
      mid:   { x: w * 0.55, y: h * 0.55 },
      end:   { x: w * 0.90, y: h * 0.48 },
    };
  }

  const from = Math.max(0, lastIdx - lookback);
  let minIdx = from;
  let minLow = candles[from]?.low ?? Infinity;
  for (let i = from; i <= lastIdx; i++) {
    const c = candles[i];
    if (isNum(c?.low) && c.low < minLow) { minLow = c.low; minIdx = i; }
  }

  const last = candles[lastIdx] || {};
  // X coordinates (fallback to proportional width if projection not ready)
  const xStart = isNum(logicalToX?.(minIdx))
    ? logicalToX(minIdx)
    : clamp(w * (0.12 + 0.75 * (minIdx / Math.max(1, n - 1))), w * 0.08, w * 0.92);

  const xLast = isNum(logicalToX?.(lastIdx))
    ? logicalToX(lastIdx)
    : clamp(w * 0.82, w * 0.08, w * 0.92);

  const xEnd = clamp((xLast ?? w * 0.82) + rightPadPx, w * 0.10, w * 0.98);

  // Y coordinates from price → pixel, nudged downward (bigger y) under the wicks.
  const yStartRaw = isNum(priceToY?.(minLow)) ? priceToY(minLow) : h * 0.62;
  const yLastRaw  = isNum(priceToY?.(last.low)) ? priceToY(last.low) : h * 0.58;

  const yStart = clamp(yStartRaw + yOffsetPx, 8, h - 8);
  const yLast  = clamp(yLastRaw  + yOffsetPx, 8, h - 8);

  // Create a straight line through (xStart,yStart) → (xLast,yLast), then extend to xEnd.
  const dx = Math.max(10, (xLast ?? 0) - (xStart ?? 0)); // avoid divide-by-zero
  const dy = (yLast ?? 0) - (yStart ?? 0);
  const tMid = 0.55;
  const tEnd = (xEnd - xStart) / dx;

  const yAt = (t) => clamp(yStart + dy * t, 8, h - 8);

  return {
    start: { x: xStart, y: yStart },
    mid:   { x: xStart + dx * tMid, y: yAt(tMid) },
    end:   { x: xEnd,               y: yAt(tEnd) },
  };
}



export function guideHorizontalAt({ priceToY, levelPrice, overlay }) {
  const h = overlay?.h || 0;
  const w = overlay?.w || 0;
  // convert the level to y px; fallback to 60% height if conversion fails
  const y = (typeof priceToY === "function" && typeof levelPrice === "number")
    ? priceToY(levelPrice)
    : h * 0.60;

  // clamp inside view
  const yy = Math.max(8, Math.min(h - 8, y));

  return {
    y: yy,
    // convenient full-width segment for teaching
    a: { x: Math.round(w * 0.06), y: yy },
    b: { x: Math.round(w * 0.94), y: yy },
  };
}

/**
 * Draw a straight guide from the **lowest wick** over a window up to the last bar,
 * to a point just to the right of the last bar — and keep it near the **tails**.
 * i1..i2 are candle indices (inclusive). If not passed, we use full range.
 */
export function guideTrendBetweenPivots({
  logicalToX,
  priceToY,
  candles,
  overlay,
  i1 = 0,
  i2 = (Array.isArray(candles) ? candles.length - 1 : 0),
  yNudgePx = -6, // small upward nudge so line sits at the bottoms of tails
}) {
  const n = Array.isArray(candles) ? candles.length : 0;
  if (!n) return { a: { x: 0, y: 0 }, b: { x: 0, y: 0 } };

  const lo = Math.max(0, Math.min(i1, i2));
  const hi = Math.max(0, Math.max(i1, i2, 0));

  // find lowest low and its index in [lo..hi-1] (exclude last bar so arrow clears it)
  let idx = lo, minLow = +Infinity;
  for (let i = lo; i < Math.max(hi, n - 1); i++) {
    const low = candles[i]?.low;
    if (typeof low === "number" && low < minLow) { minLow = low; idx = i; }
  }

  const lastIdx = n - 1;
  const last = candles[lastIdx] || {};
  const lowPx  = typeof priceToY === "function" && isFinite(minLow) ? priceToY(minLow) : overlay.h * 0.7;
  const tipY   = typeof priceToY === "function" && typeof last.close === "number" ? priceToY(last.close) : overlay.h * 0.5;

  // x positions: from lowest low bar to slightly right of the last bar
  const xA = typeof logicalToX === "function" ? logicalToX(idx) : overlay.w * 0.1;
  const xLast = typeof logicalToX === "function" ? logicalToX(lastIdx) : overlay.w * 0.85;
  const xB = Math.min(overlay.w * 0.96, (typeof xLast === "number" ? xLast : overlay.w * 0.85) + 60);

  // apply tiny upward nudge so the line hugs the tails rather than intersects bodies
  const yA = Math.max(8, Math.min(overlay.h - 8, lowPx + yNudgePx));
  const yB = Math.max(8, Math.min(overlay.h - 8, tipY  + yNudgePx));

  return { a: { x: xA, y: yA }, b: { x: xB, y: yB } };
}
